/**
 * Decompose a time series into trend, seasonal, and residual components
 * using classical multiplicative decomposition.
 */
export function decomposeSeasonal(skuId, periods, seasonLength = 12) {
    if (periods.length < seasonLength * 2) {
        // Not enough data for full decomposition — return flat estimates
        const values = periods.map((p) => p.quantity);
        const avg = values.reduce((a, b) => a + b, 0) / values.length;
        return {
            skuId,
            trend: values.map(() => avg),
            seasonal: Array(seasonLength).fill(1),
            residual: values.map(() => 0),
            seasonalStrength: 0,
            peakMonth: 1,
            troughMonth: 1,
        };
    }
    const values = periods.map((p) => p.quantity);
    const n = values.length;
    // Step 1: Calculate centered moving average (trend)
    const trend = new Array(n).fill(0);
    const halfWindow = Math.floor(seasonLength / 2);
    for (let i = halfWindow; i < n - halfWindow; i++) {
        let sum = 0;
        for (let j = i - halfWindow; j <= i + halfWindow; j++) {
            sum += values[j];
        }
        trend[i] = sum / seasonLength;
    }
    // Fill edges with nearest available trend
    for (let i = 0; i < halfWindow; i++)
        trend[i] = trend[halfWindow];
    for (let i = n - halfWindow; i < n; i++)
        trend[i] = trend[n - halfWindow - 1];
    // Step 2: Calculate seasonal indices (ratio to trend)
    const seasonalRatios = Array.from({ length: seasonLength }, () => []);
    for (let i = 0; i < n; i++) {
        if (trend[i] > 0) {
            const seasonIdx = i % seasonLength;
            seasonalRatios[seasonIdx].push(values[i] / trend[i]);
        }
    }
    const seasonal = seasonalRatios.map((ratios) => {
        if (ratios.length === 0)
            return 1;
        return ratios.reduce((a, b) => a + b, 0) / ratios.length;
    });
    // Normalize seasonal indices to average 1
    const seasonalAvg = seasonal.reduce((a, b) => a + b, 0) / seasonal.length;
    const normalizedSeasonal = seasonal.map((s) => s / seasonalAvg);
    // Step 3: Calculate residuals
    const residual = values.map((v, i) => trend[i] > 0 ? v - trend[i] * normalizedSeasonal[i % seasonLength] : 0);
    // Step 4: Calculate seasonal strength
    const residualVariance = variance(residual);
    const totalVariance = variance(values);
    const seasonalStrength = totalVariance > 0 ? Math.max(0, 1 - residualVariance / totalVariance) : 0;
    // Step 5: Find peak and trough
    let peakMonth = 0;
    let troughMonth = 0;
    let peakVal = -Infinity;
    let troughVal = Infinity;
    for (let i = 0; i < seasonLength; i++) {
        if (normalizedSeasonal[i] > peakVal) {
            peakVal = normalizedSeasonal[i];
            peakMonth = i + 1;
        }
        if (normalizedSeasonal[i] < troughVal) {
            troughVal = normalizedSeasonal[i];
            troughMonth = i + 1;
        }
    }
    return {
        skuId,
        trend,
        seasonal: normalizedSeasonal,
        residual,
        seasonalStrength,
        peakMonth,
        troughMonth,
    };
}
function variance(values) {
    if (values.length < 2)
        return 0;
    const mean = values.reduce((a, b) => a + b, 0) / values.length;
    return values.reduce((sum, v) => sum + (v - mean) ** 2, 0) / values.length;
}
//# sourceMappingURL=seasonalDecomposer.js.map